import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProjectGrantDto, ProjectRole } from '@aiai/shared';
import { deleteProjectGrant, fetchProjectGrants, upsertProjectGrant } from '../api';
import { qk } from '../api/queryKeys';
import { useCapabilities } from '../hooks/useCapabilities';
import {
  useWorkspaceProjectOptions,
  type WorkspaceProjectOption,
} from '../hooks/useWorkspaceProjectOptions';
import { Spinner } from './Spinner';

/** A grant select's value: `'none'` means "no grant" (DELETE), anything else
 *  is a `ProjectRole` to PUT. */
export type GrantSelectValue = 'none' | ProjectRole;

/** Presentational label + role select — shared by the per-member editor
 *  (backed by a mutation, current value read from the server) and the
 *  invite-time picker (backed by local form state, not yet persisted). */
export function GrantSelect({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: GrantSelectValue;
  onChange: (value: GrantSelectValue) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="truncate text-sm text-ink-muted">{label}</span>
      <select
        aria-label={`${label} access`}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value as GrantSelectValue)}
        className="input w-auto py-1 text-xs"
      >
        <option value="none">No access</option>
        <option value="viewer">Viewer</option>
        <option value="contributor">Contributor</option>
      </select>
    </div>
  );
}

function MemberGrantRow({
  project,
  userId,
}: {
  project: WorkspaceProjectOption;
  userId: string;
}) {
  const qc = useQueryClient();
  const grantsQuery = useQuery({
    queryKey: qk.projectGrants(project.id),
    queryFn: () => fetchProjectGrants(project.id),
  });

  const mutation = useMutation({
    mutationFn: (value: GrantSelectValue) =>
      value === 'none'
        ? deleteProjectGrant(project.id, userId)
        : upsertProjectGrant(project.id, userId, value),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.projectGrants(project.id) }),
  });

  const grants: ProjectGrantDto[] = grantsQuery.data ?? [];
  const currentRole: GrantSelectValue =
    grants.find((g) => g.userId === userId)?.role ?? 'none';

  return (
    <GrantSelect
      label={project.name}
      value={currentRole}
      disabled={grantsQuery.isLoading || mutation.isPending}
      onChange={(value) => mutation.mutate(value)}
    />
  );
}

/**
 * Owner-only per-member project access editor: one row per workspace
 * project/prototype with a role select (none/viewer/contributor). Reads each
 * project's grant list to find this member's current role; writes via PUT
 * (viewer/contributor) or DELETE (none), invalidating that project's grant
 * list on success.
 *
 * Gated behind `workspace:manage` — mirrors the API's own owner-only
 * enforcement on `/projects/:id/grants`, so a plain member never sees the
 * control at all (defense in depth alongside the caller hiding the expand
 * toggle in the first place).
 */
export function MemberGrantsEditor({ userId }: { userId: string }) {
  const { can } = useCapabilities();
  const { options, isLoading } = useWorkspaceProjectOptions();

  if (!can('workspace:manage')) return null;

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2 text-xs text-ink-faint">
        <Spinner className="h-3 w-3" /> Loading projects…
      </div>
    );
  }

  if (options.length === 0) {
    return <p className="py-2 text-xs text-ink-faint">No projects in this workspace yet.</p>;
  }

  return (
    <div className="divide-y divide-line border-t border-line pt-1">
      {options.map((project) => (
        <MemberGrantRow key={project.id} project={project} userId={userId} />
      ))}
    </div>
  );
}
